import { useEffect, useRef, useState } from "react";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import type { CalendarEvent, Enduser } from "@tellescope/types-models";
import { useEnduserStore } from "../../store";
import { useJoinVideoCall } from "../../hooks/useJoinVideoCall";
import { isEventCancelledForEnduser, STARTING_SOON_WINDOW_MS } from "../../hooks/useEventStatus";

// Endusers can't receive pushed updates (no sockets or data-sync for EnduserSession), so poll for the provider starting
export const WAITING_ROOM_POLL_INTERVAL_MS = 3_000;
// Providers rarely start long before the appointment, so poll gently until it's starting soon
export const WAITING_ROOM_EARLY_POLL_INTERVAL_MS = 30_000;

const getPollInterval = (event: CalendarEvent | undefined) =>
  event && event.startTimeInMS - Date.now() > STARTING_SOON_WINDOW_MS
    ? WAITING_ROOM_EARLY_POLL_INTERVAL_MS
    : WAITING_ROOM_POLL_INTERVAL_MS;

export type VideoWaitingRoomInRoomStatus = "host-absent" | "host-left";

export type VideoWaitingRoomStatus =
  | "connecting"
  | "waiting-for-host"
  | VideoWaitingRoomInRoomStatus
  | "ended"
  | "cancelled"
  | "unavailable";

const isWaitingForHost = (event: CalendarEvent | undefined, currentEnduser: Enduser | null): boolean =>
  !event ||
  (event.meetingStatus !== "live" &&
    event.meetingStatus !== "ended" &&
    !isEventCancelledForEnduser(event, currentEnduser));

/**
 * Drives the enduser video waiting room: polls the calendar event until the provider starts the meeting, then
 * connects automatically. A cached status is never trusted — the event is refetched on open before connecting.
 *
 * Pass `inRoomStatus` once the enduser is connected but the provider isn't in the room: the waiting room stays on
 * screen with that status, and neither polls nor connects again.
 */
export const useVideoWaitingRoom = (calendarEventId: string, inRoomStatus?: VideoWaitingRoomInRoomStatus) => {
  const currentEnduser = useEnduserStore((state) => state.currentEnduser);
  const hasLocalTracks = useEnduserStore(
    (state) => !!(state.videoCalling.localVideoTrack || state.videoCalling.localAudioTrack)
  );
  const { connectToCall } = useJoinVideoCall();

  const [openedAt] = useState(() => Date.now());
  const {
    data: event,
    dataUpdatedAt,
    isError,
  } = useTellescopeQuery({
    model: "calendar_events",
    method: "getOne",
    request: calendarEventId,
    enabled: !inRoomStatus,
    staleTime: 0,
    // Keep polling through a transient failure once the event has loaded; give up only if it never loaded
    refetchInterval: (query) =>
      (query.state.status !== "error" || query.state.data !== undefined) &&
      isWaitingForHost(query.state.data, currentEnduser)
        ? getPollInterval(query.state.data)
        : false,
    refetchIntervalInBackground: true,
  });

  // Only data fetched since the waiting room opened is trusted (a failed refetch leaves the cached data in place)
  const freshEvent = dataUpdatedAt >= openedAt ? event : undefined;
  const isCancelled = !!freshEvent && isEventCancelledForEnduser(freshEvent, currentEnduser);
  const isLive = freshEvent?.meetingStatus === "live" && !isCancelled;

  const status: VideoWaitingRoomStatus = inRoomStatus
    ? inRoomStatus
    : !freshEvent
    ? isError
      ? "unavailable"
      : "connecting"
    : isCancelled
    ? "cancelled"
    : freshEvent.meetingStatus === "ended"
    ? "ended"
    : isLive
    ? "connecting"
    : "waiting-for-host";

  // Joining reuses the preview tracks; connecting before they exist would create a second, leaked set.
  // Without tracks (e.g. camera denied), the waiting room keeps showing the device error and its retry.
  const canConnect = !inRoomStatus && isLive && hasLocalTracks;

  const hasStartedConnectingRef = useRef(false);
  useEffect(() => {
    // Connecting to the Twilio room is triggered by server state found by polling, not by a user event
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- no event fires when the provider starts
    if (!canConnect || hasStartedConnectingRef.current) return;
    hasStartedConnectingRef.current = true;
    connectToCall(calendarEventId);
  }, [canConnect, connectToCall, calendarEventId]);

  const startsLaterThanSoon = !!event && event.startTimeInMS - Date.now() > STARTING_SOON_WINDOW_MS;

  return {
    status,
    scheduledStartTimeInMS: status === "waiting-for-host" && startsLaterThanSoon ? event.startTimeInMS : undefined,
  };
};
